'use client';

import * as React from 'react';
import { Drawer as VaulDrawer } from 'vaul';

import { useIsMobile } from '@ui/hooks/useIsMobile';
import { cn } from '@ui/lib/utils';

import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

export interface ResponsivePopoverProps {
  /**
   * Whether the popover/sheet is open
   */
  open: boolean;
  /**
   * Callback when the open state changes
   */
  onOpenChange: (open: boolean) => void;
  /**
   * The trigger element
   */
  trigger: React.ReactNode;
  /**
   * The content to display inside the popover/sheet
   */
  children: React.ReactNode;
  /**
   * Optional footer (e.g. action buttons). On mobile it is pinned below the
   * scrollable content so it stays visible as the options scroll; on desktop
   * it renders directly under the content.
   */
  footer?: React.ReactNode;
  /**
   * Title shown in the mobile sheet header
   */
  title?: string;
  /**
   * Popover content className (desktop only)
   */
  contentClassName?: string;
  /**
   * Popover alignment (desktop only)
   * @default "start"
   */
  align?: 'start' | 'center' | 'end';
}

/**
 * A responsive popover that shows as a Popover on desktop and a bottom Sheet on mobile.
 * Follows the same pattern as ResponsiveDialog.
 */
export const ResponsivePopover = ({
  open,
  onOpenChange,
  trigger,
  children,
  footer,
  title,
  contentClassName,
  align = 'start'
}: ResponsivePopoverProps) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <>
        <div style={{ display: 'contents' }} onClick={() => onOpenChange(true)}>
          {trigger}
        </div>
        <VaulDrawer.Root open={open} onOpenChange={onOpenChange} direction="bottom" repositionInputs>
          <VaulDrawer.Portal>
            <VaulDrawer.Overlay className="fixed inset-0 z-[110] bg-zinc-900/40" />
            <VaulDrawer.Content
              aria-describedby={undefined}
              className="fixed inset-x-0 bottom-0 z-[120] mt-24 flex max-h-[95vh] flex-col gap-2 rounded-t-xl border-t bg-white p-6 pt-2 shadow-lg outline-none"
            >
              {/* Drag handle — Vaul makes this the primary drag-to-dismiss target. */}
              <VaulDrawer.Handle className="!mx-auto !mt-2 !mb-2 !h-1 !w-12 !rounded-xl !bg-border" />
              <div className="flex flex-col space-y-2 text-left">
                {title ? (
                  <VaulDrawer.Title className="text-lg font-semibold text-gray-900">{title}</VaulDrawer.Title>
                ) : (
                  <VaulDrawer.Title className="sr-only">Filter options</VaulDrawer.Title>
                )}
                <VaulDrawer.Description className="sr-only">Select a filter option</VaulDrawer.Description>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">{children}</div>
              {footer && <div className="flex-shrink-0 px-2">{footer}</div>}
            </VaulDrawer.Content>
          </VaulDrawer.Portal>
        </VaulDrawer.Root>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className={cn(contentClassName)} align={align}>
        {children}
        {footer && <div className="mt-2">{footer}</div>}
      </PopoverContent>
    </Popover>
  );
};
